import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import AccountSettings from './AccountSettings';
import KnowledgeCenter from './KnowledgeCenter';
import { LogOut, User, FileText, BookOpen } from 'lucide-react';

const Header = ({ onLoadFile }) => {
  const { currentUser, logout } = useAuth();
  const [showAccountSettings, setShowAccountSettings] = useState(false);
  const [showKnowledgeCenter, setShowKnowledgeCenter] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);

  const handleLogout = async () => {
    try {
      await logout();
      setShowUserMenu(false);
    } catch (error) {
      console.error('Failed to logout:', error);
    }
  };

  const handleLoadFile = (analysis) => {
    setShowAccountSettings(false);
    if (onLoadFile) onLoadFile(analysis);
  };

  return (
    <>
      <header className="bg-gradient-to-r from-slate-900 via-indigo-900 to-slate-900 shadow-2xl border-b-4 border-indigo-500 mb-2">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">

            {/* Logo */}
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                <span className="text-2xl font-bold text-white">🧬</span>
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="text-2xl font-bold text-white tracking-tight">GENEie</h1>
                  <span className="px-2 py-0.5 bg-yellow-400 text-yellow-900 text-[10px] font-bold rounded uppercase tracking-wider">
                    Beta
                  </span>
                </div>
                <p className="text-xs text-indigo-200">Genetic Intelligence Explorer</p>
              </div>
            </div>

            {/* User menu */}
            {currentUser && (
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center gap-3 px-4 py-2 bg-indigo-800/50 hover:bg-indigo-700/50 rounded-lg transition-colors border border-indigo-600/50"
                >
                  <div className="w-8 h-8 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-full flex items-center justify-center">
                    <User className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-left hidden sm:block">
                    <p className="text-sm font-semibold text-white">
                      {currentUser.displayName || 'User'}
                    </p>
                    <p className="text-xs text-indigo-200">{currentUser.email}</p>
                  </div>
                </button>

                {showUserMenu && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShowUserMenu(false)} />
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-2xl border border-slate-200 py-2 z-50">
                      <div className="px-4 py-3 border-b border-slate-200">
                        <p className="text-sm font-semibold text-slate-800 truncate">{currentUser.email}</p>
                      </div>

                      <button
                        onClick={() => { setShowAccountSettings(true); setShowUserMenu(false); }}
                        className="w-full px-4 py-3 text-left hover:bg-slate-50 transition-colors flex items-center gap-3"
                      >
                        <FileText className="w-5 h-5 text-indigo-600" />
                        <div>
                          <p className="text-sm font-medium text-slate-800">My Files</p>
                          <p className="text-xs text-slate-500">View saved analyses</p>
                        </div>
                      </button>

                      <button
                        onClick={() => { setShowKnowledgeCenter(true); setShowUserMenu(false); }}
                        className="w-full px-4 py-3 text-left hover:bg-slate-50 transition-colors flex items-center gap-3"
                      >
                        <BookOpen className="w-5 h-5 text-emerald-600" />
                        <div>
                          <p className="text-sm font-medium text-slate-800">Knowledge Center</p>
                          <p className="text-xs text-slate-500">Learn to read your results</p>
                        </div>
                      </button>

                      <div className="border-t border-slate-200 my-2" />

                      <button
                        onClick={handleLogout}
                        className="w-full px-4 py-3 text-left hover:bg-red-50 transition-colors flex items-center gap-3 text-red-600"
                      >
                        <LogOut className="w-5 h-5" />
                        <span className="text-sm font-medium">Sign Out</span>
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </header>

      {showAccountSettings && (
        <AccountSettings onClose={() => setShowAccountSettings(false)} onLoadAnalysis={handleLoadFile} />
      )}
      {showKnowledgeCenter && (
        <KnowledgeCenter onClose={() => setShowKnowledgeCenter(false)} />
      )}
    </>
  );
};

export default Header;